import React, { useState, useEffect, useRef, useContext } from 'react';
import { Form, Col, Row, Input, Tooltip, Checkbox, Radio, Icon, Button, Spin, message } from 'antd';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { ConfigExternalForm } from '../../containers/UserSettingsPage/styles';
import { configureServices } from '../../utils/services/externalServicesAPI';
import { withTranslation } from 'react-i18next';
import { EXTERNAL_SERVICES } from '../../containers/UserSettingsPage/TranslationValues';
import { AppStoreContext } from '../../context/appStoreContext';
const NAMESPACE = ['EXTERNAL_SERVICES'];

const ExternalServiceConfigForm = props => {
  const { data, isEditing, setData, t } = props;
  const { setLicenseDetails } = useContext(AppStoreContext);
  const { getFieldDecorator, setFieldsValue } = props.form;

  const [isProtected, setIsProtected] = useState(data?.credentials ? true : false);
  const [authType, setAuthType] = useState(
    (data?.authType === 'None' ? 'basicAuthentication' : data?.authType) || 'basicAuthentication',
  );
  const [loading, setLoading] = useState(false);

  const initializeCredentials = () => {
    if (data?.credentials) {
      return {
        [authType]: data?.credentials,
        ...(data?.authType === 'basicAuthentication'
          ? { authToken: { token: '' } }
          : { basicAuthentication: { username: '', password: '' } }),
      };
    } else {
      return {
        basicAuthentication: { username: '', password: '' },
        authToken: { token: '' },
      };
    }
  };

  const [authInputFields, setAuthInputFields] = useState(initializeCredentials());
  const credentialsRef = useRef(initializeCredentials());

  const isValid = obj => {
    for (let key in obj) {
      if (key === '' || obj[key] === '') {
        return false;
      }
    }
    return true;
  };

  const getKeyByIndex = index => {
    const keys = Object.keys(credentialsRef.current[authType]);
    if (index >= 0 && index < keys.length) {
      return keys[index];
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (data?.requestUrl) {
      setFieldsValue({ requestUrl: data?.requestUrl });
    }
  }, []);

  const validations = [
    {
      required: true,
      message: t(EXTERNAL_SERVICES.EMPTY_INPUT_ERROR_MESSAGE.key, EXTERNAL_SERVICES.EMPTY_INPUT_ERROR_MESSAGE.value),
    },
    {
      pattern: /^(?=\S)(?!\s+$).*/,
      message: t(EXTERNAL_SERVICES.BEGINNING_SPACE_NOT_ALLOWED_ERROR_MESSAGE.key, EXTERNAL_SERVICES.BEGINNING_SPACE_NOT_ALLOWED_ERROR_MESSAGE.value),
    },
  ];

  const handleChange = (authKey, index, field, value) => {
    let inputFields = Object.entries(authInputFields[authKey]);
    inputFields[index][field] = value;
    inputFields = Object.fromEntries(inputFields);
    setAuthInputFields(prev => ({ ...prev, [authKey]: inputFields }));
  };

  const handleSubmit = () => {
    if (isProtected && !isValid(authInputFields[authType])) {
      message.warning(t(EXTERNAL_SERVICES.EMPTY_INPUTS_ERROR_MESSAGE.key, EXTERNAL_SERVICES.EMPTY_INPUTS_ERROR_MESSAGE.value));
      return;
    }
    props.form.validateFields(async (err, values) => {
      if (!err) {
        try {
          setLoading(true);
          const payload = { ...values };
          payload.configType = data?.configType;
          if (isProtected) {
            payload.authType = authType;
            payload.credentials = authInputFields[authType];
          } else {
            payload.authType = 'None';
          }
          const response = await configureServices(payload);
          if (response && response?.statusCode === 5100) {
            isEditing
              ? message.success(t(EXTERNAL_SERVICES.CONFIGURATION_UPDATED_SUCCESS_MESSAGE.key, EXTERNAL_SERVICES.CONFIGURATION_UPDATED_SUCCESS_MESSAGE.value))
              : message.success(t(EXTERNAL_SERVICES.CONFIGURATION_ADDED_SUCCESS_MESSAGE.key, EXTERNAL_SERVICES.CONFIGURATION_ADDED_SUCCESS_MESSAGE.value));
            setData(prev =>
              prev.map(item =>
                item.id === data?.id ? { ...item, ...response?.data, configured: true } : item,
              ),
            );
            data?.configType === 'email_config' && setLicenseDetails(prev => ({ ...prev, EmailConfiguration: true }));
          }
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleInputChange = fieldName => e => {
    setFieldsValue({ [fieldName]: e.target.value });
  };

  const LabelWithHelpIcon = ({ label, helpText }) => {
    return (
      <>
        <span>{label}</span>
        <span>:</span>
        <Tooltip placement="top" title={helpText}>
          <i
            style={{ marginLeft: '5px' }}
            className="fa-regular fa-circle-question label-help-icon"
          ></i>
        </Tooltip>
      </>
    );
  };
  return (
    <ConfigExternalForm>
      {data.fetchingConfig ? (
        <Spin />
      ) : (
        <Form>
          <Form.Item
            label={
              <LabelWithHelpIcon
                label={t(EXTERNAL_SERVICES.API_URL_FORM_LABEL.key, EXTERNAL_SERVICES.API_URL_FORM_LABEL.value)}
                helpText={t(EXTERNAL_SERVICES.API_URL_HELP_TEXT.key, EXTERNAL_SERVICES.API_URL_HELP_TEXT.value)}
              />
            }
            colon={false}
          >
            {getFieldDecorator('requestUrl', {
              rules: validations,
            })(<Input size="medium" onChange={handleInputChange} autoFocus />)}
          </Form.Item>

          <Form.Item>
            <Checkbox onChange={e => setIsProtected(e.target.checked)} checked={isProtected}>
              {t(EXTERNAL_SERVICES.PROTECTED_CHECKBOX_FORM_LABEL.key, EXTERNAL_SERVICES.PROTECTED_CHECKBOX_FORM_LABEL.value)}
            </Checkbox>
          </Form.Item>

          {isProtected && (
            <>
              <Form.Item>
                <Radio.Group onChange={e => setAuthType(e.target.value)} value={authType}>
                  <Radio value={'basicAuthentication'}>{t(EXTERNAL_SERVICES.BASIC_AUTHENTICATION_RADIO_OPTION.key, EXTERNAL_SERVICES.BASIC_AUTHENTICATION_RADIO_OPTION.value)}</Radio>
                  <Radio value={'authToken'}>{t(EXTERNAL_SERVICES.TOKEN_RADIO_OPTION.key, EXTERNAL_SERVICES.TOKEN_RADIO_OPTION.value)}</Radio>
                </Radio.Group>
              </Form.Item>

              <SwitchTransition>
                <CSSTransition
                  key={authType}
                  classNames="fade"
                  addEndListener={(node, done) => {
                    node.addEventListener('transitionend', done, false);
                  }}
                >
                  <div>
                    <Row gutter={[8, 16]}>
                      {Object.entries(authInputFields[authType]).map(([key, val], index) => (
                        <div key={index}>
                          <Col span={8}>
                            <Input
                              value={key}
                              onChange={e => handleChange(authType, index, 0, e.target.value)}
                              placeholder="Key"
                              onBlur={e =>
                                e.target.value.trim().length === 0 &&
                                handleChange(authType, index, 0, getKeyByIndex(index))
                              }
                            />
                          </Col>
                          <Col span={16}>
                            <Input
                              value={val}
                              onChange={e => handleChange(authType, index, 1, e.target.value)}
                              placeholder="Value"
                            />
                          </Col>
                        </div>
                      ))}
                    </Row>
                  </div>
                </CSSTransition>
              </SwitchTransition>
            </>
          )}
        </Form>
      )}

      <div className="services-form-footer">
        <Button className="configure-button" onClick={handleSubmit}>
          {isEditing ? (
            <>{loading ? <Icon type="loading" /> : <Icon type="edit" />} {t(EXTERNAL_SERVICES.UPDATE_BUTTON.key, EXTERNAL_SERVICES.UPDATE_BUTTON.value)}</>
          ) : (
            <>{loading ? <Icon type="loading" /> : <Icon type="setting" />} {t(EXTERNAL_SERVICES.CONFIGURE_BUTTON.key, EXTERNAL_SERVICES.CONFIGURE_BUTTON.value)}</>
          )}
        </Button>
      </div>
    </ConfigExternalForm>
  );
};

const ExternalServiceConfig = Form.create({ name: 'external-service-form' })(
  ExternalServiceConfigForm,
);
export default withTranslation(NAMESPACE)(ExternalServiceConfig);
